import React from "react";
import { CheckCircle } from "lucide-react";
import { dataTrigger } from "@/analytics";
import { VpnImportActions } from "@/components/VpnImportActions/VpnImportActions";
import { DESKTOP_OFFER_PDF_URL } from "@/constants/offers";
import { isTelegramEnv } from "@/utils/helpers";
import { Button } from "@/ui/Button/Button";
import {
  useMainActionsContext,
  useMainScreenContext,
  useMainSubscriptionContext,
} from "../MainPageContext";
import cls from "../styles.module.scss";

function getPaymentMethodLabel(
  selectedPaymentMethod: ReturnType<
    typeof useMainSubscriptionContext
  >["selectedPaymentMethod"]
) {
  if (!selectedPaymentMethod) {
    return "Не выбрана";
  }

  return [
    selectedPaymentMethod.brand,
    selectedPaymentMethod.last4 ? `•••• ${selectedPaymentMethod.last4}` : null,
  ]
    .filter(Boolean)
    .join(" ");
}

function statusLabel(isPromo: boolean, isCancelled: boolean) {
  if (isPromo) return "Подписка по промокоду";
  if (isCancelled) return "Подписка отменена";
  return "Подписка активна";
}

export function SubscriptionBlock() {
  const isTelegram = isTelegramEnv();
  const {
    isPaid,
    isCancelled,
    isPromo,
    subscriptionEndDate,
    isConfigReady,
    configLink,
    selectedPaymentMethod,
  } = useMainSubscriptionContext();
  const { isPayLoading, onBuyClick } = useMainActionsContext();
  const { setScreen } = useMainScreenContext();

  if (!isPaid) {
    const buyLabel = isPayLoading
      ? "Открываем оплату..."
      : "Купить подписку 199₽/месяц";

    return (
      <div className={cls.buyBlock}>
        <Button
          variant="primary"
          size="lg"
          onClick={onBuyClick}
          disabled={isPayLoading}
          {...dataTrigger("bkn.main.buy")}
        >
          {buyLabel}
        </Button>

        {!isTelegram && (
          <p className={cls.offerText}>
            Нажимая кнопку &quot;Купить подписку&quot;
            <br />
            вы соглашаетесь с правилами{" "}
            <a
              href={DESKTOP_OFFER_PDF_URL}
              target="_blank"
              rel="noreferrer"
              className={cls.offerLink}
            >
              оферты
            </a>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={cls.statusCard}>
      <div className={cls.statusRow}>
        <CheckCircle className={cls.statusIcon} />
        <span className={cls.statusText}>
          {statusLabel(isPromo, isCancelled)}
        </span>
      </div>

      <p className={cls.statusSub}>Действует до {subscriptionEndDate}</p>

      {!isConfigReady && (
        <p className={cls.provisioningError}>
          Оплата прошла, но доступ пока не готов. Обновите страницу чуть позже
          или напишите в поддержку.
        </p>
      )}

      {configLink && (
        <div className={cls.configBlock}>
          <VpnImportActions configUrl={configLink} />
        </div>
      )}

      <div className={cls.paymentSummaryRow}>
        <div className={cls.paymentSummaryMeta}>
          <span className={cls.paymentSummaryTitle}>Карта</span>
          <span className={cls.paymentSummaryValue}>
            {getPaymentMethodLabel(selectedPaymentMethod)}
          </span>
        </div>

        <button
          type="button"
          className={cls.paymentActionLink}
          onClick={() => setScreen("payment-methods")}
          {...dataTrigger("bkn.main.change_card")}
        >
          Изменить
        </button>
      </div>
    </div>
  );
}
